import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  People as PeopleIcon,
  CallSplit as CallSplitIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { monitoringAPI } from '../services/api';

const TIME_OPTIONS = [
  { label: '5m', minutes: 5 },
  { label: '10m', minutes: 10 },
  { label: '30m', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: '2h', minutes: 120 },
  { label: '6h', minutes: 360 },
  { label: '12h', minutes: 720 },
  { label: '24h', minutes: 1440 },
];

const DIVIDER_SX = { borderLeft: '1px solid rgba(255,255,255,0.08)' };
const HOMER_BASE = 'http://sipcapture.sandbox-sngl.com:9080';

function buildHomerUrl(sipCallId, startEpoch, endEpoch) {
  if (!sipCallId) return null;
  const from = ((startEpoch || endEpoch) - 300) * 1000;
  const to = ((endEpoch || startEpoch) + 300) * 1000;
  const query = JSON.stringify({
    timestamp: { from, to },
    param: {
      search: {
        '1_call': { id: '', callid: [sipCallId], uuid: [] },
      },
      location: {},
      transaction: { call: true, registration: false, rest: false },
      id: {},
      timezone: { value: -180, name: 'Local' },
    },
  });
  return `${HOMER_BASE}/search/result?${query}=`;
}

function formatTimestamp(epoch) {
  if (!epoch) return 'N/A';
  return new Date(epoch * 1000).toLocaleString();
}

function getMOSColor(mos) {
  if (!mos) return 'default';
  if (mos >= 4.0) return 'success';
  if (mos >= 3.5) return 'info';
  if (mos >= 3.0) return 'warning';
  return 'error';
}

function getMOSQualityText(mos) {
  if (!mos) return 'No data available';
  if (mos >= 4.0) return 'Good quality, most users satisfied';
  if (mos >= 3.5) return 'Fair, noticeable degradation but calls are comfortable enough for most users';
  if (mos >= 3.0) return 'Users start finding the call quality annoying or difficult';
  return 'Generally considered poor/unacceptable';
}

function fmtCount(n) {
  if (n == null) return '-';
  return n.toLocaleString();
}

function fmtPct(p) {
  if (p == null) return '-';
  return `${Number(p).toFixed(2)}%`;
}

function ReasonChip({ reason }) {
  if (!reason) return <Typography variant="body2" color="text.secondary">-</Typography>;
  const colorMap = {
    'NORMAL_CLEARING': 'success',
    'USER_BUSY': 'warning',
    'NO_ANSWER': 'warning',
    'NETWORK_OUT_OF_ORDER': 'error',
  };
  return (
    <Chip
      label={reason}
      size="small"
      color={colorMap[reason] || 'info'}
      sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
    />
  );
}

function CallsDialog({ open, cluster, agentId, disconnectReason, minutes, onClose }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleExport = () => {
    if (!calls.length) return;
    const headers = ['time', 'call_id', 'agent', 'to', 'disconnect_reason', 'duration', 'mos', 'jitter_ms', 'loss_pct', 'hangup_cause'];
    const csvRows = calls.map(c => [
      c.end_epoch ? new Date(c.end_epoch * 1000).toLocaleString() : '',
      c.sip_call_id || c.uuid || '',
      c.from || '',
      c.to || '',
      c.disconnect_reason || 'UNKNOWN',
      c.duration ?? '',
      c.mos ?? '',
      c.jitter ?? '',
      c.packet_loss ?? '',
      c.hangup_cause || '',
    ]);
    const csv = [headers, ...csvRows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-calls-${cluster}-${agentId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!open || !cluster || !agentId) return;
    setLoading(true);
    setError(null);
    setCalls([]);
    monitoringAPI.getAgentBehaviourCalls(cluster, agentId, disconnectReason, minutes)
      .then(res => setCalls(res.data.calls || []))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load calls'))
      .finally(() => setLoading(false));
  }, [open, cluster, agentId, disconnectReason, minutes]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Calls - Agent {agentId} ({cluster}) - <ReasonChip reason={disconnectReason} />
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {!loading && !error && calls.length === 0 && (
          <Alert severity="info" sx={{ m: 2 }}>No calls found</Alert>
        )}

        {!loading && !error && calls.length > 0 && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Call ID</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Disconnect Reason</TableCell>
                <TableCell align="center">Duration</TableCell>
                <TableCell align="center">MOS</TableCell>
                <TableCell align="center">Jitter</TableCell>
                <TableCell align="center">Loss%</TableCell>
                <TableCell>Hangup Cause</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((call) => {
                const homerUrl = buildHomerUrl(call.sip_call_id, call.start_epoch, call.end_epoch);
                return (
                  <TableRow
                    key={call.sip_call_id || call.uuid}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/call-detail/${call.sip_call_id || call.uuid}`)}
                  >
                    <TableCell>{formatTimestamp(call.end_epoch)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      {call.sip_call_id || call.uuid || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {call.conference_name ? (
                        <a
                          href={`https://${cluster}.singlecomm.com/supervisor/call.sc?id=${call.conference_name}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#90caf9', textDecoration: 'none', fontSize: '0.82rem' }}
                        >
                          {call.to}
                        </a>
                      ) : (
                        call.to || 'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      <ReasonChip reason={call.disconnect_reason || 'UNKNOWN'} />
                    </TableCell>
                    <TableCell align="center">{call.duration || 0}s</TableCell>
                    <TableCell align="center">
                      {call.mos ? (
                        <Tooltip title={getMOSQualityText(call.mos)} arrow>
                          <Chip label={call.mos.toFixed(2)} size="small" color={getMOSColor(call.mos)} />
                        </Tooltip>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {call.jitter ? `${call.jitter.toFixed(1)}ms` : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {call.packet_loss !== null ? `${call.packet_loss.toFixed(2)}%` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <ReasonChip reason={call.hangup_cause} />
                    </TableCell>
                    <TableCell align="center">
                      {homerUrl && (
                        <Tooltip title="View in Homer">
                          <IconButton
                            component="a"
                            href={homerUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                          >
                            <TimelineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        {calls.length > 0 && (
          <Button onClick={handleExport} startIcon={<CallSplitIcon />}>
            Export CSV
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function AgentsDialog({ open, cluster, disconnectReason, minutes, onClose }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showCalls, setShowCalls] = useState(false);

  useEffect(() => {
    if (!open || !cluster || !disconnectReason) return;
    setLoading(true);
    setError(null);
    setAgents([]);
    monitoringAPI.getAgentBehaviourAgents(cluster, disconnectReason, minutes)
      .then(res => setAgents(res.data.agents || []))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load agents'))
      .finally(() => setLoading(false));
  }, [open, cluster, disconnectReason, minutes]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Agents - {cluster} - <ReasonChip reason={disconnectReason} />
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

          {!loading && !error && agents.length === 0 && (
            <Alert severity="info" sx={{ m: 2 }}>No agents found</Alert>
          )}

          {!loading && !error && agents.length > 0 && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Agent ID</TableCell>
                  <TableCell align="center">Total Calls</TableCell>
                  <TableCell align="center">Avg MOS</TableCell>
                  <TableCell align="center">Avg Jitter</TableCell>
                  <TableCell align="center">Avg Loss %</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow
                    key={agent.agent_id}
                    hover
                    onClick={() => {
                      setSelectedAgent(agent);
                      setShowCalls(true);
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{agent.agent_id}</TableCell>
                    <TableCell align="center">{fmtCount(agent.total_calls)}</TableCell>
                    <TableCell align="center">
                      {agent.avg_mos ? (
                        <Tooltip title={getMOSQualityText(agent.avg_mos)} arrow>
                          <Chip label={agent.avg_mos.toFixed(2)} size="small" color={getMOSColor(agent.avg_mos)} />
                        </Tooltip>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {agent.avg_jitter ? `${agent.avg_jitter.toFixed(1)}ms` : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {agent.avg_packet_loss ? `${agent.avg_packet_loss.toFixed(2)}%` : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View calls for this agent">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAgent(agent);
                            setShowCalls(true);
                          }}
                        >
                          <CallSplitIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {selectedAgent && (
        <CallsDialog
          open={showCalls}
          cluster={cluster}
          agentId={selectedAgent.agent_id}
          disconnectReason={disconnectReason}
          minutes={minutes}
          onClose={() => {
            setShowCalls(false);
            setSelectedAgent(null);
          }}
        />
      )}
    </>
  );
}

export default function AgentBehaviourPage() {
  const [minutes, setMinutes] = useState(60);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [showAgents, setShowAgents] = useState(false);

  const loadData = useCallback(async (m) => {
    setLoading(true);
    setError(null);
    try {
      const res = await monitoringAPI.getAgentBehaviour(m);
      setData(res.data.clusters || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load agent behaviour data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(minutes);
  }, [minutes, loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData(minutes).finally(() => setRefreshing(false));
  };

  const handleTimeChange = (event, newMinutes) => {
    if (newMinutes !== null) {
      setMinutes(newMinutes);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon />
              Agent Behaviour
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handleRefresh}
                disabled={loading || refreshing}
                title="Refresh data"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <Typography variant="body2">Time Range:</Typography>
            <ToggleButtonGroup
              value={minutes}
              exclusive
              onChange={handleTimeChange}
              size="small"
            >
              {TIME_OPTIONS.map((opt) => (
                <ToggleButton key={opt.minutes} value={opt.minutes}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Alert severity="info">No agent behaviour data available for this time range</Alert>
      ) : (
        data.map((cluster) => (
          <Card key={cluster.cluster} sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {cluster.cluster}
                </Typography>
                <Chip
                  label={`Cluster Total: ${fmtCount(cluster.cluster_total_calls)}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Disconnect Reason</TableCell>
                    <TableCell align="center">Unique Agents</TableCell>
                    <TableCell align="center">Total Calls</TableCell>
                    <TableCell align="center">Ratio of Cluster</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cluster.disconnect_reasons.map((reason) => (
                    <TableRow
                      key={reason.disconnect_reason}
                      hover
                      onClick={() => {
                        setSelectedCluster(cluster.cluster);
                        setSelectedReason(reason.disconnect_reason);
                        setShowAgents(true);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <ReasonChip reason={reason.disconnect_reason} />
                      </TableCell>
                      <TableCell align="center">{fmtCount(reason.agent_count)}</TableCell>
                      <TableCell align="center">{fmtCount(reason.total_calls)}</TableCell>
                      <TableCell align="center">{fmtPct(reason.cluster_ratio_pct)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View agents for this reason">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCluster(cluster.cluster);
                              setSelectedReason(reason.disconnect_reason);
                              setShowAgents(true);
                            }}
                          >
                            <PeopleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}

      {selectedCluster && selectedReason && (
        <AgentsDialog
          open={showAgents}
          cluster={selectedCluster}
          disconnectReason={selectedReason}
          minutes={minutes}
          onClose={() => {
            setShowAgents(false);
            setSelectedCluster(null);
            setSelectedReason(null);
          }}
        />
      )}
    </Box>
  );
}
